import { useState } from 'react';
import Button from '../../components/base/Button';
import SEOHead from '../../components/seo/SEOHead';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const services = [
    'General Check-up',
    'Dental Implants',
    'Root Canal Treatment',
    'Cosmetic Dentistry',
    'Orthodontics & Braces',
    'Teeth Whitening',
    'Pediatric Dentistry',
    'Oral Surgery',
    'Emergency Care'
  ];

  const timeSlots = [
    '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3hpomr2p8nb8r4n7b60', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ 
          name: '', 
          phone: '', 
          email: '', 
          service: '', 
          preferredDate: '', 
          preferredTime: '', 
          message: '' 
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Contact Us - The Tooth Doctor Advanced Dental Care Centre",
    "description": "Contact The Tooth Doctor for appointments and inquiries. Located in Thiruverkadu, Chennai. Call +91-6369090441 or book online.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
    "mainEntity": {
      "@type": "ContactPage",
      "about": {
        "@type": "DentalClinic",
        "name": "The Tooth Doctor - Advanced Dental Care Centre",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "No.9/3, First street, Thambu Swamy Nagar, Near Bus Stand",
          "addressLocality": "Thiruverkadu",
          "addressRegion": "Tamil Nadu",
          "postalCode": "600077",
          "addressCountry": "IN"
        },
        "telephone": "+91-6369090441",
        "email": "toothdoctor.in@gmail.com",
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "13.0827",
          "longitude": "80.2707"
        }
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Contact Us",
          "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <SEOHead
        title="Contact Us - The Tooth Doctor | Book Appointment in Chennai"
        description="Contact The Tooth Doctor for appointments and inquiries. Located in Thiruverkadu, Chennai. Call +91-6369090441 or book online for expert dental care."
        keywords="contact tooth doctor, dental appointment Chennai, book dentist appointment, dental clinic contact, Thiruverkadu dentist"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`}
        ogTitle="Contact The Tooth Doctor - Book Your Dental Appointment"
        ogDescription="Contact us for appointments and inquiries. Expert dental care in Chennai. Call +91-6369090441 or book online."
        ogUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`}
        ogImage={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-contact.jpg`}
        jsonLd={jsonLd}
      />
      {/* Modern Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#012B53] via-blue-700 to-teal-600"></div>
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20geometric%20patterns%20with%20dental%20care%20symbols%2C%20modern%20medical%20background%20design%2C%20clean%20minimalist%20style%2C%20blue%20and%20teal%20color%20scheme%2C%20professional%20healthcare%20aesthetic%2C%20contemporary%20digital%20art&width=1920&height=800&seq=contact-hero-modern&orientation=landscape')`
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center text-white">
            <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
              <i className="ri-customer-service-2-line mr-2 text-teal-300"></i>
              <span className="text-sm font-medium">Get In Touch</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
              Contact Us
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Ready to transform your smile? Connect with our expert dental team and schedule your personalized consultation today.
            </p>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/5 rounded-full blur-xl"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-teal-400/10 rounded-full blur-2xl"></div>
      </section>

      {/* Contact Cards with Modern Design */}
      <section className="py-20 -mt-12 relative z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Location Card */}
            <div className="group bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-white/20">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-map-pin-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Visit Our Clinic</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                The Tooth Doctor - Advanced Dental Care Centre<br />
                No.9/3, First street, Thambu Swamy Nagar<br />
                Near Bus Stand, Thiruverkadu<br />
                Chennai, Tamil Nadu, India
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('https://maps.app.goo.gl/6qXmz1Czz8z8tKkx7', '_blank')}
                className="group-hover:bg-blue-500 group-hover:text-white group-hover:border-blue-500 transition-all duration-300"
              >
                <i className="ri-navigation-line mr-2"></i>
                Get Directions
              </Button>
            </div>

            {/* Phone Card */}
            <div className="group bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-white/20">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Call Us</h3>
              <div className="space-y-3 mb-6">
                <p className="text-gray-600">
                  <a href="tel:+916369090441" className="hover:text-green-600 transition-colors font-medium">
                    +91 6369090441
                  </a>
                </p>
                <p className="text-gray-600">
                  <a href="tel:+919884885014" className="hover:text-green-600 transition-colors font-medium">
                    +91 9884885014
                  </a>
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('tel:+916369090441')}
                className="group-hover:bg-green-500 group-hover:text-white group-hover:border-green-500 transition-all duration-300"
              >
                <i className="ri-phone-line mr-2"></i>
                Call Now
              </Button>
            </div>

            {/* Email Card */}
            <div className="group bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-white/20">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="ri-mail-line text-white text-2xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Email Us</h3>
              <p className="text-gray-600 mb-6">
                <a href="mailto:toothdoctor.in@gmail.com" className="hover:text-purple-600 transition-colors font-medium">
                  toothdoctor.in@gmail.com
                </a>
              </p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open('mailto:toothdoctor.in@gmail.com')}
                className="group-hover:bg-purple-500 group-hover:text-white group-hover:border-purple-500 transition-all duration-300"
              >
                <i className="ri-mail-line mr-2"></i>
                Send Email
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Contact Form & Info Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-10 shadow-2xl border border-white/30">
                <div className="mb-10">
                  <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-teal-100 rounded-full mb-4">
                    <i className="ri-calendar-check-line mr-2 text-blue-600"></i>
                    <span className="text-sm font-semibold text-blue-800">Book Appointment</span>
                  </div>
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">Schedule Your Visit</h2>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    Fill out the form below and we'll get back to you within 24 hours to confirm your appointment.
                  </p>
                </div>

                <form onSubmit={handleSubmit} data-readdy-form id="contact-inquiry" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-300 hover:bg-white/70"
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-4 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-300 hover:bg-white/70"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-300 hover:bg-white/70"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="service" className="block text-sm font-semibold text-gray-7
                      00">
                      Service Needed
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-4 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8 transition-all duration-300 hover:bg-white/70"
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="preferredDate" className="block text-sm font-semibold text-gray-700">
                        Preferred Date
                      </label>
                      <input
                        type="date"
                        id="preferredDate"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-4 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all duration-300 hover:bg-white/70"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label htmlFor="preferredTime" className="block text-sm font-semibold text-gray-700">
                        Preferred Time
                      </label>
                      <select
                        id="preferredTime"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleInputChange}
                        className="w-full px-4 py-4 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8 transition-all duration-300 hover:bg-white/70"
                      >
                        <option value="">Select time</option>
                        {timeSlots.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700">
                      Additional Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-4 bg-white/50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none transition-all duration-300 hover:bg-white/70"
                      placeholder="Tell us about your dental concerns or any specific requirements..."
                    />
                    <p className="text-xs text-gray-500">{formData.message.length}/500 characters</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-2xl">
                      <div className="flex items-center">
                        <i className="ri-check-line mr-2 text-green-600"></i>
                        <span className="font-medium">Success!</span>
                      </div>
                      <p className="mt-1 text-sm">Your appointment request has been submitted. We'll contact you within 24 hours.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 text-red-800 rounded-2xl">
                      <div className="flex items-center">
                        <i className="ri-error-warning-line mr-2 text-red-600"></i>
                        <span className="font-medium">Error</span>
                      </div>
                      <p className="mt-1 text-sm">Sorry, there was an error. Please try again or call us directly.</p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg" 
                    fullWidth
                    disabled={isSubmitting}
                    className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white py-4 rounded-2xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="ri-calendar-check-line mr-2"></i>
                        Submit Appointment Request
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Additional Info Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              {/* Why Choose Us */}
              <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-3xl p-8 border border-blue-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Choose Us?</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-star-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Expert Team</h4>
                      <p className="text-gray-600 text-sm">Experienced dental professionals with advanced training</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-hospital-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Modern Equipment</h4>
                      <p className="text-gray-600 text-sm">State-of-the-art dental technology for precise treatment</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-shield-check-line text-white text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Safe & Hygienic</h4>
                      <p className="text-gray-600 text-sm">Strict sterilization protocols and safety measures</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-3xl p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center mr-4">
                    <i className="ri-alarm-warning-line text-white text-lg"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-red-800">Dental Emergency?</h3>
                </div>
                <p className="text-red-700 mb-6 leading-relaxed">
                  If you're experiencing severe tooth pain, trauma, or any dental emergency, don't wait!
                </p>
                <div className="space-y-3">
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => window.open('tel:+916369090441')}
                    className="w-full bg-red-600 hover:bg-red-700 text-white border-red-600 rounded-xl"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call Emergency Line
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open('https://wa.me/916369090441?text=Dental Emergency - Need immediate assistance', '_blank')}
                    className="w-full border-red-600 text-red-600 hover:bg-red-600 hover:text-white rounded-xl"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    WhatsApp Now
                  </Button>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="space-y-4">
                <Button 
                  variant="secondary" 
                  size="lg" 
                  fullWidth
                  onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-2xl py-4"
                >
                  <i className="ri-customer-service-2-line mr-2"></i>
                  Talk to Our AI Assistant
                </Button>
                
                <div className="grid grid-cols-2 gap-4">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open('https://maps.app.goo.gl/6qXmz1Czz8z8tKkx7', '_blank')}
                    className="rounded-xl"
                  >
                    <i className="ri-map-pin-line mr-2"></i>
                    Directions
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open('https://wa.me/916369090441', '_blank')}
                    className="rounded-xl"
                  >
                    <i className="ri-whatsapp-line mr-2"></i>
                    WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modern Google Map Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-4">
              <i className="ri-map-pin-line mr-2 text-blue-600"></i>
              <span className="text-sm font-semibold text-blue-800">Find Us</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Visit Our Clinic</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Located at Thambu Swamy Nagar, Near Bus Stand, Thiruverkadu for easy access
            </p>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-6 shadow-2xl border border-white/30">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.0686003!2d80.1217535!3d13.0686003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a526f5c4f58d151%3A0x6bc6072e4d56b3c6!2sNo.9%2F3%2C%20First%20street%2C%20Thambu%20Swamy%20Nagar%2C%20Near%20Bus%20Stand%2C%20Thiruverkadu%2C%20Chennai%2C%20Tamil%20Nadu%20600077%2C%20India!5e0!3m2!1sen!2sin!4v1703123456789"
              width="100%"
              height="500"
              style={{ border: 0, borderRadius: '20px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="The Tooth Doctor - Advanced Dental Care Centre Chennai - No.9/3, First street, Thambu Swamy Nagar, Near Bus Stand, Thiruverkadu, Chennai, Tamil Nadu, India"
              onClick={() => window.open('https://maps.app.goo.gl/44tB2jovPZYrghxy8', '_blank')}
              className="cursor-pointer"
            />
          </div>
        </div>
      </section>

      {/* Modern Office Hours */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-teal-100 rounded-full mb-4">
              <i className="ri-time-line mr-2 text-teal-600"></i>
              <span className="text-sm font-semibold text-teal-800">Operating Hours</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">When We're Open</h2>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-10 border border-white/30">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-calendar-line mr-3 text-blue-600"></i>
                  Regular Hours
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-3 px-4 bg-blue-50 rounded-xl">
                    <span className="font-medium text-gray-700">Monday - Friday</span>
                    <span className="font-bold text-blue-600">9:00 AM - 10:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3 px-4 bg-blue-50 rounded-xl">
                    <span className="font-medium text-gray-700">Saturday</span>
                    <span className="font-bold text-blue-600">9:00 AM - 9:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center py-3 px-4 bg-red-50 rounded-xl">
                    <span className="font-medium text-gray-700">Sunday</span>
                    <span className="font-bold text-red-600">4:00 PM - 9:00 PM</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <i className="ri-alarm-warning-line mr-3 text-red-600"></i>
                  Emergency Care
                </h3>
                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <p className="text-gray-700 mb-4 leading-relaxed">24/7 Emergency hotline available for urgent dental care</p>
                  <p className="font-bold text-2xl text-red-600 mb-4">+91 6369090441</p>
                  <p className="text-sm text-gray-600">
                    For urgent dental emergencies outside regular hours, call immediately for assistance.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
